import React, { useMemo } from 'react';
import cn from 'clsx';
import styles from './index.module.scss';
import Card from '@components-clean/core/card';
import DataListingProgressCard, { DataListingProgressCardRow } from '@components-clean/core/data-listing-progress-card';
import ReviewProgressCard from '@components-clean/core/review-progress-card';
import { EditReviewDto } from '@interfaces-clean/reviews.interface';
import { SubjectDataListingReviewStatusTypes } from '@interfaces-clean/subject-monitoring/subject-data-listing-review';
import { ReviewSubjectDto } from '@interfaces-clean/subject-monitoring/subject-review';
import { useTranslation } from '@hooks/index';
import { SubjectDataListingReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-data-listing-review-instance';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type Props = {
    studyRef: string;
    review: EditReviewDto;
    reviewSubjects: ReviewSubjectDto[];
};

const ReviewProgressCardsList = ({ studyRef, review, reviewSubjects }: Props) => {
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.activeReviewTab.tabs.activity.progress' });

    const totalCount = reviewSubjects.length;

    const reviewedCount = reviewSubjects.filter((s) => s.reviewed).length;

    const { data: config } = SubjectMonitoringReviewAPI.useGetConfigForReview(studyRef, review.recordId);

    const configDataListingIds = config?.dataListings.map((dl) => dl.dataListingId) ?? [];

    const { data: listingDetails } = SubjectMonitoringAPI.useGetSmDataListings(studyRef, {
        configuredDataTableIds: configDataListingIds,
    });

    const getAllDataListingReviewInstancesForReviewQuery =
        SubjectDataListingReviewInstanceApi.useGetAllDataListingReviewInstancesForReviewQuery(
            studyRef,
            config?.reviewSubject === false ? review.recordId : null, // Only run for data-listing-level reviews
        );

    const dataListingReviewRows: DataListingProgressCardRow[] = useMemo(() => {
        const allDataListingReviewInstances = getAllDataListingReviewInstancesForReviewQuery.data ?? [];

        return (
            (listingDetails ?? [])
                // Filter out any garbage data where expected values are null...
                .filter((dl) => dl.id != null && dl.name != null)
                .map((dl) => {
                    // Get reviewed instances for specific data-listing
                    const reviewedInstances = allDataListingReviewInstances
                        .filter((x) => x.dataListingId === dl.id)
                        .filter((x) => x.status === SubjectDataListingReviewStatusTypes.Reviewed);

                    return {
                        id: dl.id!,
                        name: dl.name!,
                        reviewedCount: reviewedInstances.length,
                    } satisfies DataListingProgressCardRow;
                })
        );
    }, [getAllDataListingReviewInstancesForReviewQuery.data, listingDetails]);

    return (
        <Card
            data-testid="progress-section-card"
            className={styles.gridCell}
            bodyClassName={cn(styles.gridCellInner, styles.progressCardList)}
            title={t('title')}
        >
            {/* Subject review progress is always shown */}
            <ReviewProgressCard
                title={t('subjectReviewProgress')}
                reviewedCount={reviewedCount}
                totalCount={totalCount}
            />

            {/* Data-listings review progress is shown only for data-listing-level reviews */}
            {config?.reviewSubject === false && (
                <DataListingProgressCard
                    title={t('subjectDataReviewProgress')}
                    totalCount={totalCount}
                    rows={dataListingReviewRows}
                />
            )}
        </Card>
    );
};

export default ReviewProgressCardsList;
